// @ts-strict-ignore
import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { PrintLabelingGenerator } from '@project/components/label/print-labeling/print-labeling-generator';
import { Log } from '@shared/debug/log';
import { WagoApiStatusCode } from '@shared/service/wago-api-status-code';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ApproveProjectFinalizationExportResultType, ApproveProjectFinalizationExportUtils, ProjectFinalizationInfoVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { CameraParametersVO } from '@3d-scene/camera-parameters.vo';
import { ReportGenerationPropertyUtils } from '@project/util/report-generation-property-utils';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { ReportVO } from '@project/model/vo/report.vo';
import { ReportGenerationPropertyVO } from '../../article/model/vo/property.vo';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { PrintLabelingVO } from '@project/components/label/print-labeling/print-labeling.vo';
import { StringUtils } from '@shared/util/string-utils';
import { PrintLabelingDataService } from '../../label/print-labeling/print-labeling-data.service';
import { MarkingSystemProjectCreationService } from '@configuration/wizard/service/marking-system-project-creation.service';
import { GenerationResponseVO } from '../../../manager/generation-response.vo';
import { ProjectDataService } from '../../../service/project-data.service';
import { IsProjectType } from '../../../model/is-project-type';
import { GlobalArticleContainerUtils } from '@3d-scene/model3d/util/global-article-container-utils';
import { ProjectFinalizationService } from '../../../finalization/project-finalization.service';
import { lastValueFrom } from 'rxjs';
import { UserMdmService } from '@user/service/user-mdm.service';

@Component({
  standalone: false,
  selector: 'app-finalization-preview-approval',
  templateUrl: './finalization-preview-approval.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinalizationPreviewApprovalComponent implements AfterContentInit {

  @Input() previewProperties: ReportGenerationPropertyVO[];
  @Input() previewReports: ReportVO[];
  @Input() previewLocale: string;
  @Input() previewProjectFinalizationInfo: ProjectFinalizationInfoVO;
  @Input() oldArticleNumberLong: string;

  public previewURL: string;

  constructor(
    private readonly bsModalRef: BsModalRef,
    private readonly cdRef: ChangeDetectorRef,
    private readonly projectModel: ProjectModel,
    private readonly sceneManager: SceneManager,
    private readonly projectService: ProjectService,
    private readonly projectDataService: ProjectDataService,
    private readonly alertHelper: AlertHelper,
    private readonly printLabelingDataService: PrintLabelingDataService,
    private readonly markingSystemProjectCreationService: MarkingSystemProjectCreationService,
    private readonly projectFinalizationService: ProjectFinalizationService,
    private readonly printLabelingGenerator: PrintLabelingGenerator,
    private readonly globalArticleContainerUtils: GlobalArticleContainerUtils,
    private readonly userMdmService: UserMdmService,
  ) {}

  async ngAfterContentInit(): Promise<void> {
    await this.dispatchPreviewCreationMessage();
  }

  private async dispatchPreviewCreationMessage(): Promise<void> {
    if (IsProjectType.markingSystem(this.projectModel.currentProject)) {
      await this.markingSystemProjectCreationService.updateMarkingSystemPredefinedAreas();
      this.markingSystemPredefinedAreasUpdated();
    } else {
      this.projectModel.currentProject.documentVersion += 1;
      this.sendCreateFinalizationMessage();
    }
  }

  private sendCreateFinalizationMessage(): void {
    const cameraParameters: CameraParametersVO = ReportGenerationPropertyUtils.createCameraParametersByProperties(this.previewProperties, this.sceneManager);
    this.projectFinalizationService.createFinalizationPreviewHandler({
      projectId : this.projectModel.currentProject.id,
      project: this.projectModel.currentProject,
      projectFinalizationInfo: this.previewProjectFinalizationInfo,
      userProfile: this.projectModel.userModel.userProfile,
      reports: this.previewReports,
      locale: this.previewLocale,
      reportProperties: this.previewProperties,
      cameraParameters: cameraParameters
    }).subscribe({
      next: data => this.createFinalizationPreviewCompleteHandler(data),
      error: data => this.finalizationPreviewCreatedFailure(data)
    });
  }

  public markingSystemPredefinedAreasUpdated(): void {
    this.exportToWSCX();
  }

  private exportToWSCX(): void {
    const printLabelingVO: PrintLabelingVO = this.printLabelingGenerator.createPrintLabelingByProject(this.projectModel.currentProject);
    printLabelingVO.usePlotter = true;
    const fileName: string = StringUtils.sanitizeFileName(this.previewProjectFinalizationInfo.articleNumberLong, '.wscx');
    this.printLabelingDataService.exportToWSCXForFinalization(printLabelingVO, fileName, true).subscribe({
      next: (success: string) => this.exportToWSCIX(),
      error: (error: any) => this.finalizationPreviewCreatedFailure(error)
    });
  }

  private exportToWSCIX(): void {
    let userName: string = this.projectModel.user.surname;
    if (userName == null || userName.length < 1) {
      // just in case
      userName = this.projectModel.user.login;
    }
    const printLabelingVO: PrintLabelingVO = this.printLabelingGenerator.createPrintLabelingByProject(this.projectModel.currentProject);
    const fileName: string = StringUtils.sanitizeFileName(this.previewProjectFinalizationInfo.articleNumberLong, '.wsix');
    this.printLabelingDataService.exportToWSIXForFinalization(printLabelingVO, userName, fileName, true).subscribe({
      next: (success: string) => this.sendCreateFinalizationMessage(),
      error: (error: any) => this.finalizationPreviewCreatedFailure(error)
    });
  }

  private createFinalizationPreviewCompleteHandler(data: GenerationResponseVO): void {
    this.projectModel.currentProject.documentVersion -= 1;
    if (data && data.contentGenerated) {
      this.previewURL = data.contentUrl;
      this.cdRef.detectChanges();
    } else {
      this.previewURL = 'NO URL';
    }
  }

  private async approveFinalization(previewProjectFinalizationInfo: ProjectFinalizationInfoVO): Promise<void> {
    const releaseDate = new Date();

    try {
      if (this.projectSupportsPrintConverterPdfs()) {
        if (!await this.pdmExportPrintConverterPdfs(previewProjectFinalizationInfo)) {
          this.finalizationPreviewCreatedFailure({message: 'print converter error'});
          return;
        }
      }

      const result = await lastValueFrom(this.projectDataService.approveProjectFinalizationExport(
        this.projectModel.currentProject.id,
        this.projectModel.currentProject.currentVersion,
        previewProjectFinalizationInfo,
        releaseDate,
      ));

      if (result.resultType === ApproveProjectFinalizationExportResultType.SUCCESS_WITH_OPTIONS) {
        this.projectModel.currentProject.options = result.optionValues;
      }
      const success = ApproveProjectFinalizationExportUtils.isSuccess(result);
      if (success) {
        this.userMdmService.saveLastSelectedMdmGroupForPdmExport(previewProjectFinalizationInfo.mdmGroup);
      }
      this.requestUserApprovalForFinalization(success ? "success" : "failure");

    } catch (error) {
      this.finalizationPreviewCreatedFailure(error);
    }
  }

  private projectSupportsPrintConverterPdfs(): boolean {
    return IsProjectType.mcsGds(this.projectModel.currentProject)
      || IsProjectType.ltl(this.projectModel.currentProject);
  }

  private async pdmExportPrintConverterPdfs(previewProjectFinalizationInfo: ProjectFinalizationInfoVO): Promise<boolean> {
    const printLabelingVODirect: PrintLabelingVO = this.printLabelingGenerator.createPrintLabelingVOForDirectPrinting();

    const printConverterResponses = await lastValueFrom(this.printLabelingDataService
      .exportDirectPrintingPdfsToPdm(printLabelingVODirect, previewProjectFinalizationInfo.articleNumberLong));

    previewProjectFinalizationInfo.printConverterPdfNames = [];

    const errorMessages: string[] = printConverterResponses
      .map(response => response.errorMessage)
      .filter(errorMessage => !!errorMessage);

    // Show alert if any error occurred.
    if(errorMessages.length > 0) {
      const cancelOrContinue = await this.alertHelper.showAlert(
        'printConverter.title.error',
        'printConverter.message.error_cancel_pdm_prompt',
        {'0': errorMessages.join("\n")},
        [AlertButton.CANCEL, AlertButton.NEXT]);

      if (cancelOrContinue == AlertButton.CANCEL) {
        return false;
      }
    }

    printConverterResponses
      .filter(response => !response.errorMessage)
      .map(response => response.fileName + '.pdf')
      .forEach(pdfName => previewProjectFinalizationInfo.printConverterPdfNames.push(pdfName));

    return true;
  }

  private requestUserApprovalForFinalization(status: 'success' | 'failure' | 'declinePreview'): void {
    if (status === 'success') {
      this.projectService.updateProjectData(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion);
      this.alertHelper.showAlert('finalizeProject.title.success', 'finalizeProject.message.success', {}, [AlertButton.OK], '', null, true, true);
    } else if(status == 'failure') {
      Log.warn('approveProjectFinalizationExport finished with status "failure"');
    }
    this.bsModalRef.hide();
  }

  private finalizationPreviewCreatedFailure(data: {status?: number, statusText?: string, message?: string}): void {
    if (data.status === WagoApiStatusCode.ServiceUnavailable) {
      this.alertHelper.showError('serverCommunication.title.service_not_active', 'serverCommunication.message.service_not_active');
    } else {
      this.alertHelper.showAlert(
        'finalizeProject.title.error_finalizing_configuration',
        'finalizeProject.message.error_finalizing_configuration',
        {'0': data.statusText ?? data.message},
        [AlertButton.OK]
      );
    }
    this.projectModel.currentProject.documentVersion -= 1;
    this.bsModalRef.hide();
  }

  public declineButtonClickHandler(): void {
    this.projectDataService
      .declineProjectFinalizationExport(
        this.projectModel.currentProject.id,
        this.projectModel.currentProject.currentVersion,
        this.previewProjectFinalizationInfo.articleNumberLong
      ).subscribe({
        next: (result: any) => this.requestUserApprovalForFinalization('declinePreview'),
        error: (error: any) => this.finalizationPreviewCreatedFailure(error)
      });
    this.bsModalRef.hide();
  }

  public async approveButtonClickHandler(): Promise<void> {
    try {
      const projectDimensionsInMM = this.globalArticleContainerUtils.calculateProjectDimensionsInMM();
      this.previewProjectFinalizationInfo.projectHeight = projectDimensionsInMM.height;
      this.previewProjectFinalizationInfo.projectLength = projectDimensionsInMM.depth;
      this.previewProjectFinalizationInfo.projectWidth = projectDimensionsInMM.width;
      await this.approveFinalization(this.previewProjectFinalizationInfo);
    } catch (error) {
      this.finalizationPreviewCreatedFailure(error);
    }
  }
}
